/**
 * Pushes the logged-in member's identity into the Chatwoot widget so support
 * agents see who they're talking to: name/email/phone on the contact, plus
 * platform context (member id, agent, level, balance) as custom attributes.
 *
 * Mounted once in AppShell. Reuses the same member/wallet query keys as the
 * shell, so it reads from cache and adds no extra requests. Logout reset is
 * handled by the auth store (stores/auth.ts).
 *
 * An IMPERSONATION tab syncs nothing at all. "Login as member" is a staff
 * member wearing the player's session, so svc-member answers `/members/me`
 * MASKED — `s•••@gmail.com`, `081•••5678` (ADR-0003). Pushing that into the
 * contact would overwrite the player's real email and phone in Chatwoot with a
 * row of dots, which is a mask becoming a stored value in someone else's
 * system. The rest of the payload is no better: the staff member is not the
 * player, and a contact update and a name attributed to them is a record of
 * something that did not happen. Presence already self-gates the same way.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.js';
import { isImpersonationTab } from '../impersonation-bootstrap.js';
import { usePrefsStore } from '../../stores/prefs.js';
import { useTenantStore } from '../../stores/tenant.js';
import { fetchMemberMe, MEMBER_QUERY_KEYS } from '../../features/member/api.js';
import { fetchWalletBalances, WALLET_QUERY_KEYS } from '../../features/wallet/api.js';
import { formatMoney } from '../money/index.js';
import { useBrandingStore } from '@/stores/branding.js';
import {
  setChatwootLocale,
  setChatwootUser,
  type ChatwootCustomAttributes,
} from './index.js';

export function useChatwootSync() {
  const subject = useAuthStore((s) => s.subject);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthed = Boolean(accessToken);
  const locale = usePrefsStore((s) => s.locale);
  const tenantId = useTenantStore((s) => s.tenantId);

  const memberQuery = useQuery({
    queryKey: MEMBER_QUERY_KEYS.me,
    queryFn: fetchMemberMe,
    enabled: isAuthed,
    staleTime: 30_000,
  });

  const walletQuery = useQuery({
    queryKey: WALLET_QUERY_KEYS.balances,
    queryFn: fetchWalletBalances,
    enabled: isAuthed,
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setChatwootLocale(locale);
  }, [locale]);

  const member = memberQuery.data?.member;
  const wallet = walletQuery.data;

  useEffect(() => {
    if (!isAuthed || !member || isImpersonationTab()) return;

    const fullName = [member.firstName, member.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    // `subject.currency` used to sit in this chain and is dead — both sites
    // that build a Subject hardcode 'THB'. The tenant's configured currency
    // from /site-config is the real fallback before the wallet resolves.
    const currency =
      wallet?.currency ?? useBrandingStore.getState().branding?.currency ?? 'THB';

    const attrs: ChatwootCustomAttributes = {
      username: member.username,
      member_id: member.id,
      agent_id: member.agentId ?? subject?.agentId ?? '',
      ...(tenantId ? { tenant_id: tenantId } : {}),
      status: member.status,
      member_since: member.createdAt,
      ...(member.membershipLevel != null
        ? { membership_level: member.membershipLevel }
        : {}),
      ...(member.preferredLanguage
        ? { preferred_language: member.preferredLanguage }
        : {}),
      ...(member.referralCode ? { referral_code: member.referralCode } : {}),
      ...(wallet
        ? {
            currency,
            balance: formatMoney(wallet.balance, currency),
            withdrawable: formatMoney(wallet.withdrawable, currency),
          }
        : {}),
    };

    setChatwootUser(
      member.id,
      {
        name: fullName || member.username,
        ...(member.email ? { email: member.email } : {}),
        ...(member.phone ? { phone_number: member.phone } : {}),
        // HMAC identity — present only when the backend has identity validation
        // enabled (CHATWOOT_HMAC_SECRET). Lets Chatwoot reject a spoofed id.
        ...(member.chatwootIdentifierHash
          ? { identifier_hash: member.chatwootIdentifierHash }
          : {}),
      },
      attrs,
    );
  }, [isAuthed, member, wallet, subject, tenantId]);
}
